import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import {
  useAccessEvents,
  useAccessRules,
  useClubMutation,
  useCredentials,
  useDoors,
  useGroupMembers,
  useGroups,
  useLockGrants,
  usePersonAppStatus,
  usePersons,
} from '../../data/hooks';
import {
  Badge,
  Button,
  CREDENTIAL_STATUS,
  Dialog,
  EmptyState,
  ErrorState,
  grantSyncToState,
  Loading,
  PageHeader,
  Panel,
  SyncBadge,
  useToast,
} from '../../components/ui';
import { MembershipDialog } from '../../components/MembershipDialog';
import { PersonInstallerPanel } from '../../components/ClubInstallers';
import { LoanHistoryPanel } from '../../components/LoanHistory';
import { AppStatusBadge, InviteDialog } from '../../components/AppInvite';
import { appStatus } from '../../lib/appStatus';
import { errorText } from '../../lib/errors';
import { computeScope, inScope } from '../../app/scope';
import { can } from '../../lib/roles';
import { fmtDate, fmtWeekdayDateTime } from '../../lib/format';
import { weekdaysLabel, minutesToTime } from '../../lib/weekdays';
import { EVENT_METHOD_LABEL } from './LogbookPage';
import type { Credential, CredentialType } from '../../lib/types';

export const CRED_TYPE: Record<CredentialType, string> = {
  app: 'App',
  card: 'Pas',
  fingerprint: 'Vingerafdruk',
  pin: 'Pincode',
};

export function PersonDetailPage() {
  const { personId = '' } = useParams();
  const { orgId, role, managedGroupIds, readOnly } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const navigate = useNavigate();
  const persons = usePersons(orgId);
  const groups = useGroups(orgId);
  const members = useGroupMembers(orgId);
  const rules = useAccessRules(orgId);
  const grants = useLockGrants(orgId);
  const credentials = useCredentials(orgId);
  const doors = useDoors(orgId);
  const since = useMemo(() => new Date(Date.now() - 30 * 86400_000).toISOString(), []);
  const events = useAccessEvents(orgId, { person_id: personId, since });
  const [adding, setAdding] = useState(false);
  const [revoking, setRevoking] = useState<Credential | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [appAction, setAppAction] = useState<'revoke' | 'unlink' | null>(null);
  const appStatusRows = usePersonAppStatus(orgId);
  const remove = useClubMutation(orgId, (groupId: string) => repo.removeGroupMember(groupId, personId));
  const revoke = useClubMutation(orgId, (id: string) => repo.revokeCredential(id));
  const archive = useClubMutation(orgId, () => repo.archivePerson(personId));
  const revokeInvite = useClubMutation(orgId, (inviteId: string) => repo.revokePersonInvite(inviteId));
  const unlinkApp = useClubMutation(orgId, () => repo.unlinkPersonApp(personId));

  const qs = [persons, groups, members, rules, grants, credentials, doors];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const scope = computeScope(role, managedGroupIds, members.data!, rules.data!);
  const person = persons.data!.find((p) => p.id === personId);
  if (!person || !inScope(scope, 'personIds', person.id)) {
    return <EmptyState title="Persoon niet gevonden" action={<Link to={`/c/${orgId}/personen`}>Terug naar personen</Link>} />;
  }

  const myMemberships = members.data!.filter((m) => m.person_id === personId);
  const groupById = new Map(groups.data!.map((g) => [g.id, g]));
  const doorByLock = new Map(doors.data!.filter((d) => d.lock_id).map((d) => [d.lock_id!, d]));
  const myGrants = grants.data!.filter((g) => g.person_id === personId && g.sync_state !== 'revoked');
  const myCreds = credentials.data!.filter((c) => c.person_id === personId);
  const canEditMembership = can('group_member.edit', role) && !readOnly;
  const editableGroups = groups.data!.filter((g) => inScope(scope, 'groupIds', g.id) && !myMemberships.some((m) => m.group_id === g.id));
  const app = appStatus(
    person,
    appStatusRows.data?.find((r) => r.person_id === person.id),
  );
  const canInvite = can('person.invite', role) && !readOnly;
  const canAppAdmin = can('person.app_admin', role) && !readOnly;

  return (
    <div className="stack-lg">
      <PageHeader
        title={person.full_name}
        intro={[person.external_ref ? `Relatienummer ${person.external_ref}` : null, person.email, person.is_minor ? 'Minderjarig' : null]
          .filter(Boolean)
          .join(', ')}
        actions={
          <>
            {canEditMembership && editableGroups.length > 0 && (
              <Button variant="primary" onClick={() => setAdding(true)}>
                Toevoegen aan groep
              </Button>
            )}
            {can('person.archive', role) && !readOnly && (
              <Button variant="danger" onClick={() => setArchiving(true)}>
                Archiveren
              </Button>
            )}
          </>
        }
      />

      {person.is_loan_card && <LoanHistoryPanel orgId={orgId} personId={person.id} />}

      {!person.is_loan_card && (
        <>
          <Panel
            title="App"
            description="Met de Antreja-app opent iemand deuren met de telefoon. Inloggen gaat met het e-mailadres en een code."
            actions={
              <span className="row">
                {canInvite && app.kind !== 'active' && (
                  <Button variant={app.kind === 'none' ? 'primary' : 'secondary'} size="sm" onClick={() => setInviting(true)}>
                    {app.kind === 'none' ? 'Uitnodigen voor de app' : 'Opnieuw uitnodigen'}
                  </Button>
                )}
                {canAppAdmin && app.inviteId && app.kind === 'invited' && (
                  <Button size="sm" onClick={() => setAppAction('revoke')}>
                    Uitnodiging intrekken
                  </Button>
                )}
                {canAppAdmin && app.kind === 'active' && (
                  <Button size="sm" variant="danger" onClick={() => setAppAction('unlink')}>
                    App ontkoppelen
                  </Button>
                )}
              </span>
            }
          >
            <div className="panel-body row" style={{ gap: 8 }}>
              <AppStatusBadge status={app} />
              {app.inviteEmail && <span className="muted small">naar {app.inviteEmail}</span>}
              {app.kind === 'none' && !person.email && <span className="muted small">Nog geen e-mailadres bekend.</span>}
            </div>
          </Panel>

          <PersonInstallerPanel
            orgId={orgId}
            personId={person.id}
            personName={person.full_name}
            hasApp={!!person.profile_id}
            editable={can('installer.club_assign', role) && !readOnly}
          />
        </>
      )}

      <Panel title="Groepen" description="Toegang loopt altijd via een groep.">
        {myMemberships.length === 0 ? (
          <p className="tile-empty">Zit in geen enkele groep, dus heeft geen toegang.</p>
        ) : (
          <ul className="tile-list">
            {myMemberships.map((m) => {
              const g = groupById.get(m.group_id);
              const mayEdit = canEditMembership && inScope(scope, 'groupIds', m.group_id);
              return (
                <li key={m.group_id}>
                  <span>
                    <Link className="primary" to={`/c/${orgId}/groepen/${m.group_id}`}>
                      {g?.name}
                    </Link>
                    <span className="cell-sub">{m.valid_until ? `Tot en met ${fmtDate(m.valid_until)}` : 'Geen einddatum'}</span>
                  </span>
                  {mayEdit && (
                    <Button
                      size="sm"
                      onClick={async () => {
                        await remove.mutateAsync(m.group_id);
                        toast(`Uit ${g?.name} gehaald. Intrekking wacht op sync.`);
                      }}
                      disabled={remove.isPending}
                    >
                      Uit groep halen
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel title="Toegang per deur" description="Uitgerekend uit de groepen. Opgeslagen is nog niet hetzelfde als op het slot.">
        {myGrants.length === 0 ? (
          <p className="tile-empty">Geen deuren.</p>
        ) : (
          <div className="table-wrap">
            <table className="data stack-mobile">
              <thead>
                <tr>
                  <th scope="col">Deur</th>
                  <th scope="col">Wanneer</th>
                  <th scope="col">Einddatum</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {myGrants.map((g) => (
                  <tr key={g.id}>
                    <td className="cell-main cell-title">{doorByLock.get(g.lock_id)?.name ?? 'Onbekende deur'}</td>
                    <td data-label="Wanneer">
                      {g.schedule.map((s, i) => (
                        <span key={i} className="cell-sub" style={{ color: 'inherit' }}>
                          {weekdaysLabel(s.weekdays)},{' '}
                          {s.start_minute === 0 && s.end_minute === 1440
                            ? 'hele dag'
                            : `${minutesToTime(s.start_minute)}–${minutesToTime(s.end_minute)}`}
                        </span>
                      ))}
                    </td>
                    <td data-label="Einddatum">{g.valid_until ? fmtDate(g.valid_until) : 'Geen'}</td>
                    <td>
                      <SyncBadge state={grantSyncToState(g.sync_state)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {role !== 'group_manager' && (
        <Panel
          title="Middelen"
          description="App, passen en vingerafdrukken. Een pas of vingerafdruk wordt per slot ingeleerd door de installateur."
        >
          {myCreds.length === 0 ? (
            <p className="tile-empty">Nog geen middelen.</p>
          ) : (
            <ul className="tile-list">
              {myCreds.map((c) => (
                <li key={c.id}>
                  <span>
                    <span className="primary">
                      {CRED_TYPE[c.type]}
                      {c.label && c.label !== CRED_TYPE[c.type] ? `: ${c.label}` : ''}
                    </span>
                    <span className="cell-sub">
                      {c.lock_id ? (doorByLock.get(c.lock_id)?.name ?? 'slot') : 'alle deuren met toegang'},{' '}
                      {c.valid_until ? `tot ${fmtDate(c.valid_until)}` : 'geen einddatum'}
                      {c.enrolled_by ? `, ingeleerd ${fmtDate(c.created_at)}` : ''}
                    </span>
                  </span>
                  <span className="row">
                    <Badge tone={CREDENTIAL_STATUS[c.status].tone}>{CREDENTIAL_STATUS[c.status].label}</Badge>
                    {can('credential.revoke', role) && !readOnly && (c.status === 'active' || c.status === 'pending_enrollment') && (
                      <Button size="sm" variant="danger" onClick={() => setRevoking(c)}>
                        {c.type === 'card' ? 'Pas blokkeren' : 'Intrekken'}
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <Panel title="Logboek, afgelopen 30 dagen">
        {events.isLoading ? (
          <Loading />
        ) : (events.data ?? []).length === 0 ? (
          <p className="tile-empty">Geen events.</p>
        ) : (
          <ul className="tile-list">
            {events.data!.slice(0, 15).map((e) => (
              <li key={e.id}>
                <span>
                  <span className="primary">{e.door_id ? doors.data!.find((d) => d.id === e.door_id)?.name : 'Onbekende deur'}</span>
                  <span className="cell-sub">
                    {fmtWeekdayDateTime(e.occurred_at)}, {EVENT_METHOD_LABEL[e.method].toLowerCase()}
                  </span>
                </span>
                {e.result === 'granted' ? <Badge tone="green">Open</Badge> : <Badge tone="red">Geweigerd</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {adding && <MembershipDialog open onOpenChange={setAdding} groups={editableGroups} persons={persons.data!} fixedPerson={person} />}

      {revoking && (
        <Dialog
          open
          onOpenChange={(o) => !o && setRevoking(null)}
          title={revoking.type === 'card' ? 'Pas blokkeren' : 'Middel intrekken'}
          description="Antreja zet de intrekking direct klaar. Op het slot is hij pas actief na de volgende sync; tot dan werkt het middel nog."
        >
          <div className="alert alert-warn">
            Loop bij voorkeur met de Antreja-app langs de deur
            {revoking.lock_id ? `: ${doorByLock.get(revoking.lock_id)?.name}` : 'en'} om de intrekking meteen door te zetten.
          </div>
          <div className="dialog-actions">
            <Button onClick={() => setRevoking(null)}>Annuleren</Button>
            <Button
              variant="danger"
              disabled={revoke.isPending}
              onClick={async () => {
                await revoke.mutateAsync(revoking.id);
                toast('Ingetrokken in Antreja. Wacht op sync met het slot.');
                setRevoking(null);
              }}
            >
              {revoking.type === 'card' ? 'Pas blokkeren' : 'Intrekken'}
            </Button>
          </div>
        </Dialog>
      )}

      {inviting && <InviteDialog person={person} status={app} onClose={() => setInviting(false)} />}

      {appAction && (
        <Dialog
          open
          onOpenChange={(o) => !o && setAppAction(null)}
          title={appAction === 'revoke' ? 'Uitnodiging intrekken' : 'App ontkoppelen'}
          description={
            appAction === 'revoke'
              ? `De uitnodiging voor ${person.full_name} werkt daarna niet meer.`
              : `${person.full_name} kan met de app geen deuren van jullie club meer openen. Het Antreja-account zelf blijft bestaan (bijvoorbeeld voor een andere club). Passen en vingerafdrukken blijven werken.`
          }
        >
          {(revokeInvite.error || unlinkApp.error) && (
            <div className="alert alert-danger" role="alert">
              {errorText(revokeInvite.error ?? unlinkApp.error, 'Niet gelukt.')}
            </div>
          )}
          <div className="dialog-actions">
            <Button onClick={() => setAppAction(null)}>Annuleren</Button>
            <Button
              variant="danger"
              disabled={revokeInvite.isPending || unlinkApp.isPending}
              onClick={async () => {
                try {
                  if (appAction === 'revoke' && app.inviteId) await revokeInvite.mutateAsync(app.inviteId);
                  if (appAction === 'unlink') await unlinkApp.mutateAsync(undefined);
                  toast(appAction === 'revoke' ? 'Uitnodiging ingetrokken.' : 'App ontkoppeld.');
                  setAppAction(null);
                } catch {
                  /* melding staat in de dialoog */
                }
              }}
            >
              {appAction === 'revoke' ? 'Intrekken' : 'Ontkoppelen'}
            </Button>
          </div>
        </Dialog>
      )}

      {archiving && (
        <Dialog
          open
          onOpenChange={setArchiving}
          title={`${person.full_name} archiveren`}
          description="De persoon verliest alle toegang. Historie in het logboek blijft bewaard."
        >
          <div className="dialog-actions">
            <Button onClick={() => setArchiving(false)}>Annuleren</Button>
            <Button
              variant="danger"
              disabled={archive.isPending}
              onClick={async () => {
                await archive.mutateAsync(undefined);
                toast(`${person.full_name} gearchiveerd. Intrekkingen wachten op sync.`);
                navigate(`/c/${orgId}/personen`);
              }}
            >
              Archiveren
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
